import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import PersonaRoutine, { Moment } from '@comp/persona/persona-routine'
import PersonaHelpLinks, { HelpLinkGroup } from '@comp/persona/persona-help-links'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  routineEyebrow: string
  routineTitle: string
  routineBody: string
  moments: Moment[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  helpEyebrow: string
  helpTitle: string
  helpBody: string
  helpGroups: HelpLinkGroup[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Monofor for security engineers',
    metaDescription:
      'Phishing-resistant MFA, brokered PAM, anomaly detection, and tamper-evident audit. The identity control plane that security teams actually want to operate.',
    heroEyebrow: 'For security engineers',
    heroTitle: ['Identity as a', 'security control plane.'],
    heroBody:
      'Phishing-resistant MFA, brokered privileged access, anomaly detection, and tamper-evident audit: every primitive a modern identity-security program needs.',
    badges: ['FIDO2 / WebAuthn', 'Brokered PAM with recording', 'Tamper-evident audit'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    routineEyebrow: 'A day with Monofor',
    routineTitle: 'A day in the life of a security engineer.',
    routineBody:
      'Less alert noise, less standing privilege, more leverage when the moment counts.',
    moments: [
      {
        time: '09:00',
        title: 'Risk-anomaly triage',
        body: 'Monosign anomaly alerts hit Slack overnight: three impossible-travel attempts. You scrub the live session view, kill one suspicious session, and step up the user to a hardware key by lunch.',
      },
      {
        time: '13:00',
        title: 'Change-window PAM session',
        body: 'A DBA needs root on a production Oracle node. They request elevation through Monopam, you approve from your phone, and the session records full keystrokes for the next 45 minutes.',
      },
      {
        time: '17:30',
        title: 'Quarterly access review',
        body: 'Monosync flags 14 entitlements that drifted past their review date. You bulk-approve the obvious ones and revoke the unattested. The exported evidence pack lands in the GRC tool in one click.',
      },
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'Four controls. One platform.',
    pillarsBody:
      'The capabilities that move the needle on credential phishing, privileged-account abuse, and audit findings.',
    pillars: [
      {
        title: 'Phishing-resistant MFA, everywhere',
        description:
          'FIDO2, WebAuthn, push, biometrics, and hardware keys, applied on web, desktop, VPN, RDP, SSH. The attack surface for credential phishing collapses.',
        bullets: [
          'FIDO2 / WebAuthn with platform authenticators',
          'Push approval through Monofor Identity',
          'Hardware-key support (YubiKey, Titan, Feitian)',
          'MFA on the Windows logon screen',
        ],
      },
      {
        title: 'Privileged access, brokered and recorded',
        description:
          'Every privileged session is brokered through Monopam. Credentials never enter human hands; sessions are recorded with keystroke transcription.',
        bullets: [
          'Vault every privileged secret',
          'Brokered RDP, SSH, web, and database sessions',
          'Just-in-time elevation tied to change tickets',
          'Searchable session recordings with alerts',
        ],
      },
      {
        title: 'Anomaly detection on every authentication',
        description:
          'Risk-aware policies score device, network, and behavior on every sign-in. Step up MFA when something looks off; alert when it stays off.',
        bullets: [
          'Risk scoring on device and network',
          'Impossible-travel and IP-anomaly rules',
          'Step-up MFA on sensitive actions',
          'Anomaly alerts into your SIEM',
        ],
      },
      {
        title: 'Audit evidence that holds up',
        description:
          'Immutable, tamper-evident logs. Cryptographic chain-of-custody on exports. Built for SOC 2, ISO 27001, GDPR, KVKK, PCI-DSS.',
        bullets: [
          'Hash-chained audit logs',
          'Signed evidence bundle exports',
          'Pre-built reports per framework',
          'SIEM streaming via subscribe-to-audits',
        ],
      },
    ],
    helpEyebrow: 'Straight to the docs',
    helpTitle: 'Docs aimed at your threat model.',
    helpBody:
      'Deep-links to the help articles security engineers actually live in.',
    helpGroups: [
      {
        title: 'MFA & passwordless',
        links: [
          { label: 'Authenticators overview', description: 'Configure factors and policies', href: 'https://help.monofor.com/monosign/authenticators' },
          { label: 'New authenticator', description: 'Enroll FIDO2 / OTP / push factors', href: 'https://help.monofor.com/monosign/new-authenticator' },
          { label: 'Windows passwordless login', href: 'https://help.monofor.com/monosign/windows-passwordless-login' },
          { label: 'macOS passwordless login', href: 'https://help.monofor.com/monosign/macos-passwordless-login' },
        ],
      },
      {
        title: 'Privileged access (Monopam)',
        links: [
          { label: 'Vault overview', description: 'Vault every privileged secret', href: 'https://help.monofor.com/monopam/vault' },
          { label: 'Access automation', description: 'Brokered password injection', href: 'https://help.monofor.com/monopam/access-automation' },
          { label: 'Session recordings', description: 'Search, replay, export', href: 'https://help.monofor.com/monopam/recordings' },
          { label: 'Privilege & policies', href: 'https://help.monofor.com/monopam/privilege' },
        ],
      },
      {
        title: 'Audit & detection',
        links: [
          { label: 'Audits', description: 'Immutable, exportable audit logs', href: 'https://help.monofor.com/monosign/audits' },
          { label: 'Subscribe to audits', description: 'Stream events to your SIEM', href: 'https://help.monofor.com/monosign/subscribe-to-audits' },
          { label: 'Access restrictions', description: 'Lock down by IP, geo, device', href: 'https://help.monofor.com/monosign/access-restrictions' },
          { label: 'Zones and zone definitions', href: 'https://help.monofor.com/monosign/zones' },
        ],
      },
      {
        title: 'Hardening',
        links: [
          { label: 'Security overview', href: 'https://help.monofor.com/monosign/security' },
          { label: 'Monopam encryption methodology', href: 'https://help.monofor.com/monopam/what-encryption-methodology-that-monopam-use' },
          { label: 'AV exclusions for Monopam Gateway', href: 'https://help.monofor.com/monopam/how-to-test-av-exclusions-exceptions' },
          { label: 'Kerberos authentication', href: 'https://help.monofor.com/monosign/kerberos-authentication' },
        ],
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'For IT Admins', description: 'JML, password reset, dashboards, sync.', href: '/for/it-admins' },
      { label: 'For Compliance Officers', description: 'Audit, attestation, framework reports.', href: '/for/compliance-officers' },
      { label: 'Zero Trust', description: 'Use case: identity-first Zero Trust.', href: '/solutions/zero-trust' },
    ],
  },
  tr: {
    metaTitle: 'Güvenlik mühendisleri için Monofor',
    metaDescription:
      'Kimlik avına dirençli MFA, aracılı PAM, anomali tespiti ve kurcalamaya karşı korumalı denetim. Güvenlik ekiplerinin gerçekten işletmek isteyeceği kimlik kontrol düzlemi.',
    heroEyebrow: 'Güvenlik mühendisleri için',
    heroTitle: ['Bir güvenlik kontrol düzlemi', 'olarak kimlik.'],
    heroBody:
      'Kimlik avına dirençli MFA, aracılı ayrıcalıklı erişim, anomali tespiti ve kurcalamaya karşı korumalı denetim: modern bir kimlik güvenliği programının ihtiyaç duyduğu tüm temel yapı taşları.',
    badges: ['FIDO2 / WebAuthn', 'Kayıt altına alınan aracılı PAM', 'Kurcalamaya karşı korumalı denetim'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    routineEyebrow: 'Monofor ile bir gün',
    routineTitle: 'Bir güvenlik mühendisinin bir günü.',
    routineBody:
      'Daha az uyarı gürültüsü, daha az kalıcı ayrıcalık, kritik anda daha çok kaldıraç.',
    moments: [
      {
        time: '09:00',
        title: 'Risk anomalisi triyajı',
        body: 'Gece boyunca Monosign anomali uyarıları Slack’e düşmüş: üç imkânsız seyahat denemesi. Canlı oturum görünümünü tarıyor, şüpheli bir oturumu sonlandırıyor ve öğlene kadar kullanıcıyı donanım anahtarına yükseltiyorsunuz.',
      },
      {
        time: '13:00',
        title: 'Değişiklik penceresinde PAM oturumu',
        body: 'Bir DBA, üretimdeki bir Oracle düğümünde root erişimine ihtiyaç duyuyor. Monopam üzerinden yükseltme talep ediyor, siz telefonunuzdan onaylıyorsunuz ve oturum sonraki 45 dakika boyunca tüm tuş vuruşlarıyla kaydediliyor.',
      },
      {
        time: '17:30',
        title: 'Üç aylık erişim gözden geçirmesi',
        body: 'Monosync, gözden geçirme tarihini aşan 14 yetkiyi işaretliyor. Bariz olanları toplu onaylıyor, doğrulanmayanları iptal ediyorsunuz. Dışa aktarılan kanıt paketi tek tıkla GRC aracına düşüyor.',
      },
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Dört kontrol. Tek platform.',
    pillarsBody:
      'Kimlik bilgisi avcılığı, ayrıcalıklı hesap kötüye kullanımı ve denetim bulguları konusunda ibreyi oynatan yetenekler.',
    pillars: [
      {
        title: 'Her yerde kimlik avına dirençli MFA',
        description:
          'FIDO2, WebAuthn, push, biyometri ve donanım anahtarları; hepsi web, masaüstü, VPN, RDP ve SSH’te uygulanır. Kimlik bilgisi avcılığının saldırı yüzeyi çöker.',
        bullets: [
          'Platform doğrulayıcılarıyla FIDO2 / WebAuthn',
          'Monofor Identity ile push onayı',
          'Donanım anahtarı desteği (YubiKey, Titan, Feitian)',
          'Windows oturum açma ekranında MFA',
        ],
      },
      {
        title: 'Aracılı ve kayıt altında ayrıcalıklı erişim',
        description:
          'Her ayrıcalıklı oturum Monopam üzerinden aracılanır. Kimlik bilgileri asla insan eline geçmez; oturumlar tuş vuruşu dökümüyle kaydedilir.',
        bullets: [
          'Her ayrıcalıklı sırrı kasaya alın',
          'Aracılı RDP, SSH, web ve veritabanı oturumları',
          'Değişiklik kayıtlarına bağlı tam zamanında yükseltme',
          'Uyarı destekli, aranabilir oturum kayıtları',
        ],
      },
      {
        title: 'Her kimlik doğrulamada anomali tespiti',
        description:
          'Risk farkındalıklı politikalar her oturum açmada cihazı, ağı ve davranışı puanlar. Bir şey ters göründüğünde MFA’yı kademelendirin; ters kalmaya devam ederse uyarı alın.',
        bullets: [
          'Cihaz ve ağ üzerinde risk puanlama',
          'İmkânsız seyahat ve IP anomalisi kuralları',
          'Hassas işlemlerde kademeli MFA',
          'SIEM’inize anomali uyarıları',
        ],
      },
      {
        title: 'Ayakta kalan denetim kanıtı',
        description:
          'Değiştirilemez, kurcalamaya karşı korumalı loglar. Dışa aktarımlarda kriptografik gözetim zinciri. SOC 2, ISO 27001, GDPR, KVKK ve PCI-DSS için tasarlandı.',
        bullets: [
          'Hash zincirli denetim logları',
          'İmzalı kanıt paketi dışa aktarımları',
          'Çerçeve bazında hazır raporlar',
          'Denetim aboneliğiyle SIEM akışı',
        ],
      },
    ],
    helpEyebrow: 'Doğrudan dokümantasyona',
    helpTitle: 'Tehdit modelinize göre dokümanlar.',
    helpBody:
      'Güvenlik mühendislerinin gerçekten içinde yaşadığı yardım makalelerine derin bağlantılar.',
    helpGroups: [
      {
        title: 'MFA ve parolasız',
        links: [
          { label: 'Doğrulayıcılara genel bakış', description: 'Faktörleri ve politikaları yapılandırın', href: 'https://help.monofor.com/monosign/authenticators' },
          { label: 'Yeni doğrulayıcı', description: 'FIDO2 / OTP / push faktörlerini kaydedin', href: 'https://help.monofor.com/monosign/new-authenticator' },
          { label: 'Windows parolasız oturum açma', href: 'https://help.monofor.com/monosign/windows-passwordless-login' },
          { label: 'macOS parolasız oturum açma', href: 'https://help.monofor.com/monosign/macos-passwordless-login' },
        ],
      },
      {
        title: 'Ayrıcalıklı erişim (Monopam)',
        links: [
          { label: 'Kasaya genel bakış', description: 'Her ayrıcalıklı sırrı kasaya alın', href: 'https://help.monofor.com/monopam/vault' },
          { label: 'Erişim otomasyonu', description: 'Aracılı parola enjeksiyonu', href: 'https://help.monofor.com/monopam/access-automation' },
          { label: 'Oturum kayıtları', description: 'Arayın, yeniden oynatın, dışa aktarın', href: 'https://help.monofor.com/monopam/recordings' },
          { label: 'Ayrıcalık ve politikalar', href: 'https://help.monofor.com/monopam/privilege' },
        ],
      },
      {
        title: 'Denetim ve tespit',
        links: [
          { label: 'Denetimler', description: 'Değiştirilemez, dışa aktarılabilir denetim logları', href: 'https://help.monofor.com/monosign/audits' },
          { label: 'Denetimlere abone olma', description: 'Olayları SIEM’inize aktarın', href: 'https://help.monofor.com/monosign/subscribe-to-audits' },
          { label: 'Erişim kısıtlamaları', description: 'IP, coğrafya ve cihaza göre kısıtlayın', href: 'https://help.monofor.com/monosign/access-restrictions' },
          { label: 'Bölgeler ve bölge tanımları', href: 'https://help.monofor.com/monosign/zones' },
        ],
      },
      {
        title: 'Sıkılaştırma',
        links: [
          { label: 'Güvenliğe genel bakış', href: 'https://help.monofor.com/monosign/security' },
          { label: 'Monopam şifreleme metodolojisi', href: 'https://help.monofor.com/monopam/what-encryption-methodology-that-monopam-use' },
          { label: 'Monopam Gateway için AV istisnaları', href: 'https://help.monofor.com/monopam/how-to-test-av-exclusions-exceptions' },
          { label: 'Kerberos kimlik doğrulaması', href: 'https://help.monofor.com/monosign/kerberos-authentication' },
        ],
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'BT yöneticileri için', description: 'JML, parola sıfırlama, panolar, senkronizasyon.', href: '/for/it-admins' },
      { label: 'Uyum sorumluları için', description: 'Denetim, erişim onayları, çerçeve raporları.', href: '/for/compliance-officers' },
      { label: 'Sıfır Güven', description: 'Kullanım senaryosu: kimlik öncelikli Sıfır Güven.', href: '/solutions/zero-trust' },
    ],
  },
}

type Props = { locale: string }

export default function SecurityEngineersPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/for/security-engineers"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <PersonaRoutine
          eyebrow={c.routineEyebrow}
          title={c.routineTitle}
          body={c.routineBody}
          moments={c.moments}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={c.pillarsTitle}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <PersonaHelpLinks
          eyebrow={c.helpEyebrow}
          title={c.helpTitle}
          body={c.helpBody}
          groups={c.helpGroups}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
